import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Box, Stack } from "@mui/material";
import { useNavigate } from "react-router-dom";
import TopBar from "../../../../../../component/TopBar";
import MainBox from "../../../../container/MainBox";
import DataTable from "../../../../../../component/DataTable";
import BtnPrimary from "../../../../../../component/PrimaryButton";
import BtnSecondary from "../../../../../../component/SecondaryButton";
import InlineMessage from "../../../../../../component/InlineMessage";
import { StatusChip, PersonCell } from "../../../../../../component/listing";
import { counterRowSx } from "../../../../container/frameLayout";
import { getAllChecklistProgress, getOnboardingTemplates } from "../../../../../../services/onboarding";
import { ensureOrgDepartments } from "../../../../../../services/useOrgData";
import { useNotify } from "../../../../../../component/NotificationProvider";
import { tokens, displayFont, motion, chartColors } from "../../../../../../theme";
import { pressable } from "../../../../../../component/pressableProps";
import Modal from "../../../../../../component/Modal";
import EmployeeForm from "./OnboardingForm";
import {
  joinerState,
  blockingParty,
  blockingReason,
  cohortStats,
  stateCounts,
  sortByStart,
  stepBreakdown,
  owedBy,
  nextDays,
  daysUntilStart,
  BUCKETS,
  OWNER_LABEL,
  OWNER_BLOCKING,
} from "./onboardingCohort";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
/** "1 Apr" — spelled from a table, because ICU renders "Sept". */
const shortDate = (value) => {
  if (!value) return "—";
  const s = String(value);
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (!m) return "—";
  return `${Number(m[3])} ${MONTHS[Number(m[2]) - 1]}`;
};

const TONE_INK = { danger: tokens.danger, warning: tokens.warningText, success: tokens.successText };

const Counter = ({ label, value, sub, tone }) => (
  <Box sx={{ background: tokens.card, border: `1px solid ${tokens.border}`, borderRadius: "10px", p: "1rem 1.125rem" }}>
    <Box sx={{ fontSize: "0.75rem", fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: tokens.textMuted, mb: "0.625rem" }}>
      {label}
    </Box>
    <Box sx={{ fontFamily: displayFont, fontSize: "1.375rem", fontWeight: 700, lineHeight: 1.15, fontVariantNumeric: "tabular-nums", color: TONE_INK[tone] || tokens.text }}>
      {value}
    </Box>
    {sub != null && <Box sx={{ fontSize: "0.75rem", color: tokens.textMuted, mt: "0.25rem" }}>{sub}</Box>}
  </Box>
);

/**
 * A panel. Two title treatments, because the book uses two: the wide panel
 * carries an Archivo heading with a caption under it, and the narrow pair use
 * 13px small caps with no caption at all — a caption in a 20rem column costs a
 * line and says nothing the title has not.
 */
const Panel = ({ title, caption, children, small, sx }) => (
  <Box sx={{ flex: 1, minWidth: 0, background: tokens.card, border: `1px solid ${tokens.border}`, borderRadius: "10px", p: "1.375rem", ...sx }}>
    {small ? (
      <Box sx={{ fontSize: "0.8125rem", fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: tokens.textMuted, mb: "1rem" }}>
        {title}
      </Box>
    ) : (
      <>
        <Box sx={{ fontFamily: displayFont, fontSize: "1.0625rem", fontWeight: 700, color: tokens.text }}>{title}</Box>
        {caption && <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, mt: "0.1875rem", mb: "1.125rem" }}>{caption}</Box>}
      </>
    )}
    {children}
  </Box>
);

/** Party colours, so a swatch means the same party in both narrow panels. */
const OWNER_COLOR = {
  employee: chartColors[0],
  it: chartColors[2],
  admin: chartColors[1],
  manager: chartColors[3],
};

/**
 * The cohort view.
 *
 * The record page answers how one person is doing; this answers who is
 * blocking. It sorts by start date rather than by name, because a joiner three
 * days out matters more than one three weeks out.
 */
/**
 * The cohort screen, for joining or for leaving.
 *
 * One component for both, because the question is the same shape — who is
 * mid-flight, what is outstanding, who is blocked — and the data behind it is
 * literally the same collection with a different `phase`. A second screen
 * would be the same table with different nouns, and would drift.
 *
 * What differs is wording, the pivot date, and whether you can add somebody:
 * you add a joiner, but nobody is "added" to leaving — an exit begins by
 * starting notice on a person who already exists.
 */
const COPY = {
  onboarding: {
    heading: "Onboarding",
    person: "joiner",
    pivot: "Starts",
    blurb: "Joiners with a checklist appear here.",
    empty: "Nobody is onboarding",
    templateHref: "/app/employees/onboarding/template",
  },
  offboarding: {
    heading: "Offboarding",
    person: "leaver",
    pivot: "Last day",
    blurb: "People on notice appear here, with what is left to do before they go.",
    empty: "Nobody is leaving",
    templateHref: "/app/employees/offboarding/template",
  },
};

const Cohort = ({ phase = "onboarding" }) => {
  const copy = COPY[phase] || COPY.onboarding;
  const navigate = useNavigate();
  const notify = useNotify();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [bucket, setBucket] = useState("all");
  const [adding, setAdding] = useState(false);
  // Whether the organisation has said what onboarding means. An empty tracker
  // has two very different causes and this is the one nothing surfaced:
  // employee.js assigns a checklist at hire only `if (template)`, so with no
  // template nobody has ever been given one, and no amount of hiring will
  // change that. null while unknown, so the empty state does not accuse
  // anybody before the answer is in.
  const [hasTemplate, setHasTemplate] = useState(null);
  const [departments, setDepartments] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      const res = await getAllChecklistProgress(phase);
      if (res?.success === false) throw new Error(res?.message || "Failed to load onboarding");
      setRows(Array.isArray(res?.data) ? res.data : []);
    } catch (e) {
      setFailed(true);
      notify.error(e?.message || "Failed to load onboarding");
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let active = true;
    ensureOrgDepartments()
      .then((list) => active && setDepartments(Array.isArray(list) ? list : []))
      // Only used to name a column. Failing to load leaves the dash, which is
      // the same thing it shows for somebody with no team.
      .catch(() => {});
    return () => { active = false; };
  }, []);

  useEffect(() => {
    let active = true;
    getOnboardingTemplates(phase)
      .then((res) => {
        if (!active) return;
        const list = Array.isArray(res?.data) ? res.data : [];
        setHasTemplate(list.some((t) => t?.isActive !== false));
      })
      // Only used to explain an empty table. Failing to answer leaves the
      // wording as it was rather than claiming there is no template.
      .catch(() => active && setHasTemplate(null));
    return () => { active = false; };
  }, []);

  const stats = useMemo(() => cohortStats(rows), [rows]);
  const counts = useMemo(() => stateCounts(rows), [rows]);
  const steps = useMemo(() => stepBreakdown(rows), [rows]);
  const owed = useMemo(() => owedBy(rows), [rows]);
  const upcoming = useMemo(() => nextDays(rows, 14), [rows]);

  const visible = useMemo(() => {
    const predicate = BUCKETS[bucket] || BUCKETS.all;
    return sortByStart(rows).filter((r) => predicate(joinerState(r)));
  }, [rows, bucket]);

  /**
   * The one joiner whose block costs money. Named rather than counted: "2
   * blocked" is a statistic, and a person with a payroll deadline is not.
   */
  const worst = useMemo(() => {
    const started = rows
      .filter((r) => !r?.progress?.complete)
      .map((r) => ({ r, until: daysUntilStart(r) }))
      .filter((x) => x.until != null && x.until < 0)
      .sort((a, b) => a.until - b.until)[0];
    if (!started) return null;
    const party = blockingParty(started.r);
    const name = `${started.r.employee?.fname || ""} ${started.r.employee?.lname || ""}`.trim();
    return {
      name,
      days: Math.abs(started.until),
      reason: blockingReason(started.r),
      party: party ? OWNER_BLOCKING[party.owner] : null,
    };
  }, [rows]);

  const CHIPS = [
    { key: "all", label: "Everyone", n: rows.length },
    { key: "inProgress", label: "In progress", n: counts.inProgress },
    { key: "blocked", label: "Blocked", n: counts.blocked },
    { key: "starting", label: "Starting soon", n: counts.starting },
    { key: "complete", label: "Complete", n: counts.complete },
  ];

  // Accepts the populated object too, in case an endpoint starts sending one.
  const teamName = (value) => {
    if (!value) return "—";
    if (typeof value === "object") return value.name || "—";
    return departments.find((d) => String(d?._id) === String(value))?.name || "—";
  };

  const columns = [
    {
      key: "joiner",
      label: "Joiner",
      width: "24%",
      render: (r) => (
        <PersonCell
          name={`${r.employee?.fname || ""} ${r.employee?.lname || ""}`.trim() || "Unnamed"}
          secondary={r.employee?.designation || ""}
          src={r.employee?.profileImage}
        />
      ),
    },
    {
      key: "team",
      label: "Team",
      cellSx: { color: tokens.textMuted },
      // Employee.department is a String holding a department id, so `.name` is
      // never set and the old fallback printed the raw id — six people on this
      // screen showed "6a3208aa443f2f4ab9cfa2ec" where a team name belongs.
      // Looked up instead, and an id that resolves to nothing shows as "—":
      // a dangling reference is missing data, and an id is not a team name
      // under any circumstances.
      render: (r) => teamName(r.employee?.department),
    },
    {
      key: "starts",
      label: copy.pivot,
      width: "6rem",
      cellSx: { whiteSpace: "nowrap" },
      // The date the checklist hangs off: when they arrive, or when they go.
      render: (r) =>
        shortDate(phase === "offboarding" ? r.employee?.lastWorkingDay : r.employee?.joiningDate),
    },
    {
      key: "checklist",
      label: "Checklist",
      width: "9rem",
      /**
       * The count, the percentage, then the bar underneath.
       *
       * "4 of 9" and "44%" are the same fact twice, and the book states both
       * deliberately: the count is what you act on — four steps to chase — and
       * the percentage is what you compare against the person in the row below.
       * The bar sits under them rather than beside, so the two numbers read as
       * one line and the column stays narrow.
       *
       * Green, not plum: this is progress toward done, and it is the same green
       * the step breakdown uses for a settled step.
       */
      render: (r) => {
        const p = r.progress || {};
        const settled = p.requiredSettled ?? p.done ?? 0;
        const total = p.requiredTotal ?? p.total ?? 0;
        const percent = Math.max(0, Math.min(100, Number(p.percent) || 0));
        return (
          <Box>
            <Stack direction="row" alignItems="baseline" spacing={0.875} sx={{ mb: "0.375rem" }}>
              <Box sx={{ fontSize: "0.8125rem", fontWeight: 600, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                {settled} of {total}
              </Box>
              <Box sx={{ fontSize: "0.75rem", color: tokens.textMuted, fontVariantNumeric: "tabular-nums" }}>
                {percent}%
              </Box>
            </Stack>
            <Box sx={{ height: "0.375rem", borderRadius: "3px", background: tokens.border, overflow: "hidden" }}>
              <Box
                sx={{
                  width: `${percent}%`,
                  height: "100%",
                  background: tokens.success,
                  transition: `width ${motion.duration[3]}ms ${motion.easing.decelerate}`,
                }}
              />
            </Box>
          </Box>
        );
      },
    },
    {
      key: "blocking",
      label: "Blocking",
      width: "22%",
      /**
       * A party, not a step. The useful action is a nudge to a person, so the
       * chip says who and the line beneath says what they owe.
       */
      render: (r) => {
        const state = joinerState(r);
        const party = blockingParty(r);
        const label =
          state.key === "complete"
            ? "Complete"
            : party
            ? `Waiting on ${OWNER_LABEL[party.owner] === "New joiners" ? "the joiner" : OWNER_LABEL[party.owner]}`
            : state.label;
        return (
          <Box>
            <StatusChip label={label} tone={state.tone} />
            <Box sx={{ fontSize: "0.75rem", color: tokens.textMuted, mt: "0.25rem" }}>
              {blockingReason(r)}
            </Box>
          </Box>
        );
      },
    },
    {
      key: "open",
      label: "",
      width: "5rem",
      cellSx: { py: "0.5rem" },
      render: (r) => (
        <BtnSecondary
          onClick={(e) => { e.stopPropagation(); navigate(`/app/employee-details/${r.employee?._id}/onboarding`); }}
          sx={{ borderRadius: "6px", px: "0.75rem", py: "0.375rem", fontSize: "0.8125rem", minWidth: 0 }}
        >
          Open
        </BtnSecondary>
      ),
    },
  ];

  return (
    <Box sx={{ width: "100%" }}>
      <TopBar MainHeading={copy.heading} breadcrumbs={[{ label: "People", href: "/app/employees" }, { label: copy.heading }]} />
      <MainBox sx={{ padding: "1.25rem" }}>
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ md: "flex-start" }} spacing={1.5} sx={{ mb: "1.25rem" }}>
          <Box>
            <Box sx={{ fontFamily: displayFont, fontSize: "1.5rem", fontWeight: 700, color: tokens.text }}>{copy.heading}</Box>
            <Box sx={{ fontSize: "0.875rem", color: tokens.textMuted, mt: "0.25rem" }}>
              {loading
                ? " "
                : [
                    `${stats.inProgress} ${copy.person}${stats.inProgress === 1 ? "" : "s"} in progress`,
                    stats.blocked ? `${stats.blocked} blocked` : null,
                    `${stats.stepsOutstanding} step${stats.stepsOutstanding === 1 ? "" : "s"} outstanding across the cohort`,
                  ].filter(Boolean).join(" · ")}
            </Box>
          </Box>
          <Stack direction="row" spacing={1}>
            <BtnSecondary to={copy.templateHref} sx={{ borderRadius: "8px" }}>Checklist template</BtnSecondary>
            {/* A modal, not a page: adding a joiner is a task you do while
                looking at the cohort, and coming back to a list you had
                filtered is the normal ending. */}
            {/* Only for joining. Nobody is "added" to leaving — an exit
                begins by starting notice on somebody who already exists. */}
            {phase === "onboarding" && (
              <BtnPrimary onClick={() => setAdding(true)} sx={{ borderRadius: "8px" }}>
                Add a joiner
              </BtnPrimary>
            )}
          </Stack>
        </Stack>

        <Box sx={{ ...counterRowSx(5), mb: "1.25rem" }}>
          <Counter label="In progress" value={stats.inProgress} sub={stats.inProgress ? "not yet finished" : "nobody mid-flight"} />
          <Counter
            label="Starting this week"
            value={stats.startingThisWeek.length}
            sub={stats.startingThisWeek.length ? shortDate(stats.startingThisWeek[0]?.employee?.joiningDate) : "none in the next 7 days"}
          />
          <Counter
            label="Blocked"
            value={stats.blocked}
            sub={stats.blocked ? `waiting on ${stats.blockedOwners.map((o) => OWNER_LABEL[o]).join(" and ")}` : "nothing overdue"}
            tone={stats.blocked ? "danger" : undefined}
          />
          <Counter
            label="Steps outstanding"
            value={stats.stepsOutstanding}
            sub={`${stats.stepsDue} currently due`}
            tone={stats.stepsDue ? "warning" : undefined}
          />
          <Counter label="Complete" value={stats.complete} sub="finished onboarding" />
        </Box>

        {/* The one joiner whose block costs money, named rather than counted. */}
        {worst && (
          <InlineMessage tone="danger" sx={{ mb: "1.25rem" }}>
            <b>{worst.name} started {worst.days} day{worst.days === 1 ? "" : "s"} ago</b> and is still
            incomplete{worst.party ? `, waiting on ${worst.party}` : ""} — {worst.reason}. Onboarding
            that runs past the start date holds up payroll enrolment.
          </InlineMessage>
        )}

        <Stack direction="row" spacing={1} sx={{ mb: "1rem", flexWrap: "wrap", rowGap: 1 }}>
          {CHIPS.map((c) => {
            const active = bucket === c.key;
            return (
              <Box
                key={c.key}
                {...pressable(() => setBucket(c.key))}
                sx={{
                  fontSize: "0.8125rem", fontWeight: active ? 600 : 400, cursor: "pointer",
                  px: "0.875rem", py: "0.5rem", borderRadius: "8px",
                  color: active ? tokens.card : tokens.textMuted,
                  background: active ? tokens.primary : "transparent",
                  border: `1px solid ${active ? tokens.primary : tokens.borderStrong}`,
                  "&:hover": { background: active ? tokens.primary : tokens.surfaceSubtle },
                }}
              >
                {c.label} {c.n}
              </Box>
            );
          })}
          <Box sx={{ flex: 1 }} />
          <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, alignSelf: "center" }}>
            Sorted by start date
          </Box>
        </Stack>

        <DataTable
          rows={visible}
          loading={loading}
          error={failed}
          onRetry={load}
          errorTitle="Couldn't load onboarding"
          empty={
            hasTemplate === false
              ? {
                  title: `No ${copy.heading.toLowerCase()} template yet`,
                  description:
                    "A joiner is given a checklist from your template when they are added, so until one exists nobody appears here. Create one in Settings → Onboarding templates, then backfill anyone already hired from their profile.",
                }
              : { title: "Nobody is onboarding", description: "Joiners with a checklist appear here." }
          }
          onRowClick={(r) => navigate(`/app/employee-details/${r.employee?._id}/onboarding`)}
          tableSx={{ width: "100%", tableLayout: "fixed" }}
          columns={columns}
        />

        {/* All three stacked, full width.
            "Next 14 days" is sentences and "Where the cohort is stuck" is a
            four-column grid with a bar in it; both read badly in a third of a
            row. The book's own container wraps rather than forcing a row. */}
        <Stack spacing={2.5} sx={{ mt: "1.25rem" }}>
          <Panel title="Where the cohort is stuck" caption={`The standard steps, across ${stats.inProgress} joiner${stats.inProgress === 1 ? "" : "s"}`}>
            {steps.length ? (
              <Box>
                {steps.map((st) => (
                  <Box
                    key={st.label}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1.5fr 6.875rem 1fr 5.25rem",
                      gap: "0.875rem",
                      alignItems: "center",
                      py: "0.6875rem",
                      borderBottom: `1px solid ${tokens.border}`,
                      fontSize: "0.875rem",
                    }}
                  >
                    <Box sx={{ fontWeight: 500, color: tokens.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {st.label}
                    </Box>
                    <Box sx={{ fontSize: "0.75rem", color: tokens.textMuted }}>
                      {OWNER_LABEL[st.owner] || st.owner}
                    </Box>
                    <Box sx={{ display: "flex", height: "0.5rem", borderRadius: "4px", background: tokens.border, overflow: "hidden" }}>
                      <Box sx={{ width: `${st.total ? (st.done / st.total) * 100 : 0}%`, background: tokens.success }} />
                      {/*
                        The outstanding part takes the owning party's colour
                        rather than one flat amber, so this panel and "Who owes
                        what" beside it share a colour language: a row of blue
                        gaps says IT is the hold-up without reading a word.
                      */}
                      <Box sx={{ width: `${st.total ? (st.outstanding / st.total) * 100 : 0}%`, background: OWNER_COLOR[st.owner] || tokens.accent }} />
                    </Box>
                    <Box sx={{ textAlign: "right", fontSize: "0.8125rem", fontVariantNumeric: "tabular-nums", color: st.outstanding ? tokens.warningText : tokens.textMuted }}>
                      {st.outstanding ? `${st.outstanding} due` : "clear"}
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : (
              <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>Nothing outstanding across the cohort.</Box>
            )}
          </Panel>

          <Panel title="Who owes what" small>
            {owed.length ? (
              <Box>
                {owed.map((o) => (
                  <Box
                    key={o.owner}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 4rem 5.625rem",
                      gap: "0.75rem",
                      alignItems: "center",
                      py: "0.6875rem",
                      borderBottom: `1px solid ${tokens.border}`,
                      fontSize: "0.875rem",
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1.125} sx={{ minWidth: 0 }}>
                      {/* The swatch ties a party to the same colour it has in
                          the step breakdown beside this panel. */}
                      <Box sx={{ width: "0.5625rem", height: "0.5625rem", borderRadius: "2px", background: OWNER_COLOR[o.owner], flex: "none" }} />
                      <Box sx={{ color: tokens.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{o.label}</Box>
                    </Stack>
                    <Box sx={{ textAlign: "right", fontWeight: 600, fontVariantNumeric: "tabular-nums", color: tokens.text }}>
                      {o.count}
                    </Box>
                    <Box sx={{ textAlign: "right" }}>
                      {/* HR is us. Everyone else is somebody to chase. */}
                      <StatusChip label={o.owner === "admin" ? "You" : "Chase"} tone={o.owner === "admin" ? "info" : "warning"} />
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : (
              <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>Nobody owes anything.</Box>
            )}
          </Panel>

          <Panel title="Next 14 days" small>
            {upcoming.length ? (
              <Stack spacing={1.625}>
                {upcoming.map((d) => (
                  <Stack key={d.day} direction="row" spacing={1.625} alignItems="flex-start">
                    <Box sx={{ width: "3.25rem", flex: "none", fontSize: "0.75rem", color: tokens.textMuted, fontVariantNumeric: "tabular-nums", pt: "2px" }}>
                      {shortDate(d.date.toISOString())}
                    </Box>
                    <Box sx={{ flex: 1, fontSize: "0.8125rem", lineHeight: 1.5, color: tokens.text }}>
                      {d.parts.map((part, i) => (
                        <Box key={i} sx={{ mb: i < d.parts.length - 1 ? "0.375rem" : 0 }}>
                          <Box component="span" sx={{ fontWeight: 600 }}>{part.lead}</Box>{" "}
                          {part.detail}
                        </Box>
                      ))}
                    </Box>
                  </Stack>
                ))}
              </Stack>
            ) : (
              <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>Nothing due in the next fortnight.</Box>
            )}
          </Panel>
        </Stack>
      </MainBox>

      <Modal open={adding} onClose={() => setAdding(false)} width="52rem">
        <EmployeeForm
          onCancel={() => setAdding(false)}
          onDone={() => { setAdding(false); load(); }}
        />
      </Modal>
    </Box>
  );
};

export default Cohort;
